import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Navigation, Router } from '@angular/router';
import { FilterModel } from 'ag-grid-community';
import { User } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { UserAccountTypes } from 'src/app/interfaces/user-account-types';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsersComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private SharedDataService: SharedDataService
  ) {
    this.SharedDataService.listenForEvent<Navigation>().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'SetNavigationData' && data) {
          const {
            extras: { state }
          } = data;

          if (state) {
            this.handleStateData(state);
          }
        }
      }
    });

    this.SharedDataService.sendEvent('GetNavigationData');

    // A bit of overlap with the service above.
    const currentNavigation = this.router.currentNavigation();
    const state = currentNavigation?.extras?.state;
    const paramMap = this.route.snapshot.paramMap;
    const CompanyId = Number(paramMap.get('CompanyId'));

    if (CompanyId) {
      this.title = 'Admin - Client Users';
      Object.assign(this.suppliedGridQueryData, { CompanyId });
    }

    if (state) {
      this.handleStateData(state);
    }
  }

  title: string = 'Users';
  loggedInUserType!: UserAccountTypes;
  LoggedInUser: User = this.AuthService.getUser();
  showDeleted: boolean = false;
  reloadGrid: boolean = false;

  FilterModel?: FilterModel;

  suppliedGridQueryData: {
    [key: string]: any;
  } = {
    Contact: {}
  };

  // Display these actions in the action column.
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit']
  };

  UserTypeToRouteMap = {
    Admin: 'admin',
    Client: `client/${this.AuthService.getUserCompanyId()}/admin`,
    Vendor: `vendor/${this.AuthService.getUserVendorId()}`,
    'External Vive Level 1': 'admin',
    'External Vive Level 2': 'admin'
  };

  showAddUserButton() {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw new Error('Missing user permission.');
    }

    return !new Set([
      'ADMIN_PRIME_TEAM',
      'ADMIN_EMAILS_TEAM',
      'ADMIN_INSURANCE_TEAM',
      'ADMIN_FRAUD_TEAM',
      'ADMIN_CUSTOMER_SERVICE_TEAM'
    ]).has(role);
  }

  onShowDeleteChange() {
    this.showDeleted = !this.showDeleted;

    if (this.showDeleted) {
      Object.assign(this.suppliedGridQueryData, {
        deleted: { $in: [true, false] }
      });
    } else {
      Object.assign(this.suppliedGridQueryData, { deleted: false });
    }

    this.reloadGrid = true;
  }

  handleStateData(state: GenericObject) {
    const { query, FilterModel, title } = state;

    if (title) {
      this.title = this.title.concat(` - ${title}`);
    }

    if (FilterModel) {
      this.FilterModel = FilterModel;
    }

    if (query) {
      Object.assign(this.suppliedGridQueryData, query);
    }
  }

  addEditUser(User?: User) {
    const navigationArray: (string | number)[] = [
      this.UserTypeToRouteMap[this.loggedInUserType],
      'user'
    ];

    if (User) {
      const { id } = User;
      if (!id) {
        throw this.ToastService.addErrorToast('Error', 'Missing User Id.', {
          returnError: true
        });
      }
      navigationArray.push(id);
    }

    this.router.navigate(navigationArray);
  }

  // Pass in selected row.
  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'edit') {
      this.addEditUser(rowData as User);
    }
  }

  ngOnInit(): void {
    this.AuthService.getUserType().subscribe({
      next: (userType: UserAccountTypes) => {
        this.loggedInUserType = userType;
      }
    });
  }
}
